import React from 'react';
import {
  Box,
  Typography,
  Tooltip
} from '@mui/material';
import {
  Info as InfoIcon
} from '@mui/icons-material';

const BusinessRatings = ({ 
  profileData,
  hasValidSEOData,
  validateRating
}) => {
  if (!hasValidSEOData) {
    return (
      <Box sx={{ my: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          This business hasn't received enough reviews yet to display ratings.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Be the first to help others discover this business by writing a review!
        </Typography>
      </Box>
    );
  }

  // Calculate weighted reference percentage based on connection levels
  const calculateWeightedReferencePercentage = () => {
    if (!profileData.reviews || profileData.reviews.length === 0) {
      return 0;
    }

    let totalWeightedScore = 0;
    let totalPossibleWeight = 0;

    profileData.reviews.forEach(review => {
      const connectionLevel = review.connectionLevel || 0;
      let weight = 0;

      // Assign weights based on connection level
      if (connectionLevel === 0) {
        weight = 0; // Not connected
      } else if (connectionLevel === 1) {
        weight = 0.3; // Direct connection
      } else if (connectionLevel === 2) {
        weight = 0.7; // 2-hop connection
      } else if (connectionLevel >= 3) {
        weight = 1; // 3+ hop connection (highest trust)
      }

      totalWeightedScore += weight;
      totalPossibleWeight += 1; // Each review could potentially have max weight of 1
    });

    // Calculate percentage: (weighted score / total possible) * 100
    const percentage = totalPossibleWeight > 0 
      ? Math.round((totalWeightedScore / totalPossibleWeight) * 100)
      : 0;

    return percentage;
  };

  const referencePercentage = calculateWeightedReferencePercentage();

  // Helper function to get reference percentage color
  const getReferenceColor = (percentage) => {
    if (percentage < 20) return 'error.main'; // Red
    if (percentage >= 20 && percentage < 60) return 'warning.main'; // Yellow/Orange
    return 'success.main'; // Green
  };

  // Helper function to get rating color (1-5 scale)
  const getRatingColor = (rating) => {
    if (rating < 2) return 'error.main'; // Red
    if (rating >= 2 && rating < 3.5) return 'warning.main'; // Yellow/Orange
    return 'success.main'; // Green
  };

  // Helper function to get review count color
  const getReviewCountColor = (count) => {
    if (count < 5) return 'error.main'; // Red
    if (count >= 5 && count < 15) return 'warning.main'; // Yellow/Orange
    return 'success.main'; // Green
  };

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        my: 2,
        px: 1,
      }}
      itemProp="aggregateRating"
      itemScope
      itemType="https://schema.org/AggregateRating"
    >
      {/* Meta tags - only rendered when we have valid data */}
      <meta itemProp="ratingValue" content={validateRating(profileData.avgSkillRating_all).toString()} />
      <meta itemProp="reviewCount" content={profileData.reviews.length.toString()} />
      <meta itemProp="bestRating" content="5" />
      <meta itemProp="worstRating" content="1" />
      
      {/* Traditional Rating */}
      <Box 
        sx={{ 
          flex: '1 1 0', 
          minWidth: { xs: '45%', sm: '100px' },
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          position: 'relative',
          py: 1,
          '&:not(:last-child)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '25%',
            height: '50%',
            width: '1px',
            backgroundColor: 'divider',
            display: { xs: 'none', sm: 'block' }
          }
        }}
      >
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontWeight: 500 }}
        >
          Traditional
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'medium',
            color: getRatingColor(profileData.avgSkillRating_all),
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {`${Math.floor(profileData.avgSkillRating_all*10)/10}`}
          <Box component="span" sx={{ fontSize: '0.8em', ml: 0.5 }}>★</Box>
        </Typography>
      </Box>
      
      {/* Smart Rating */}
      <Box 
        sx={{ 
          flex: '1 1 0', 
          minWidth: { xs: '45%', sm: '100px' },
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          position: 'relative',
          py: 1,
          '&:not(:last-child)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '25%',
            height: '50%',
            width: '1px',
            backgroundColor: 'divider',
            display: { xs: 'none', sm: 'block' }
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ fontWeight: 500 }}
          >
            Smart Rating
          </Typography>
          <Tooltip title="Smart ratings use advanced algorithms to weigh reviewer credibility and connection strength">
            <InfoIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'medium',
            color: getRatingColor(profileData.SkillValue_all),
            display: 'flex',
            alignItems: 'center'
          }}
        >
          {Math.round(profileData.SkillValue_all*10)/10}
          <Box component="span" sx={{ fontSize: '0.8em', ml: 0.5 }}>★</Box>
        </Typography>
      </Box>
      
      {/* Reference Percentage */}
      <Box 
        sx={{ 
          flex: '1 1 0', 
          minWidth: { xs: '45%', sm: '100px' },
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          position: 'relative',
          py: 1,
          '&:not(:last-child)::after': {
            content: '""',
            position: 'absolute',
            right: 0,
            top: '25%',
            height: '50%',
            width: '1px',
            backgroundColor: 'divider',
            display: { xs: 'none', sm: 'block' }
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ fontWeight: 500 }}
          >
            Reference
          </Typography>
          <Tooltip title="Weighted trust score based on reviewer connection strength: Direct (30%), 2-hop (70%), 3+ hop (100%)">
            <InfoIcon sx={{ fontSize: 12, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'medium',
            color: getReferenceColor(referencePercentage),
          }}
        >
          {referencePercentage}%
        </Typography>
      </Box>
      
      {/* Total Reviews */}
      <Box 
        sx={{ 
          flex: '1 1 0', 
          minWidth: { xs: '45%', sm: '100px' },
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          py: 1
        }}
      >
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ fontWeight: 500 }}
        >
          Reviews
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 'medium',
            color: getReviewCountColor(profileData.reviews.length),
          }}
        >
          {profileData.reviews.length}
        </Typography>
      </Box>
    </Box>
  );
};

export default BusinessRatings;